import React, { useState, useEffect } from "react";

import { isEmpty } from "lodash";
import Select from "react-select";
import { Trans, useTranslation } from "react-i18next";

import StepsNavButtons from "../Buttons/StepsNavButtons";
import TitleWithDescription from "../Headers/TitleWithDescription";
import SimpleNotification from "../Notifications/SimpleNotification";
import VersionTable from "../Tables/VersionTable";
import ProductTags from "../Tags/ProductTags";
import ModelVersionsAPI from "../../API/modelVersions.api";
import LoadingOverlay from "../Loaders/LoadingOverlay";
import Pagination from "../Pagination/Pagination";
import { getReactSelectTheme } from "../../utils/ReactSelectStyles";
import BaseAlertModal from "../Modals/BaseAlertModal";
import { setObject, getObject } from "../../utils/StorageHelpers";
import { BOOLEAN_DROPDOWN } from "../../utils/constants";
import InformationTooltip from "../Tooltips/InformationTooltip";

const SelectModel = ({
    nextStepButtonText,
    nextStep,
    models,
    parametersDictionary,
    setParametersDictionary,
    removedParameters,
    setRemovedParameters,
    selectedModel,
    setSelectedModel,
    selectedParameterVersion,
    setSelectedParameterVersion,
    isNewVersion,
    product,
    setIsForceUpgradeEnabled,
}) => {
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [selectedParameter, setSelectedParameter] = useState({});
    const [isSelectedParamHidden, setIsSelectedParamHidden] = useState(
        BOOLEAN_DROPDOWN.false
    );
    const [versionsTableData, setVersionsTableData] = useState([]);
    const [isVersionTableLoading, setIsVersionTableLoading] = useState(false);
    const [versionsCurrentPage, setVersionsCurrentPage] = useState(1);
    const [versionsTotalCount, setVersionsTotalCount] = useState(0);
    //Used to temp save the model that will be updated if confirmed with modal
    const [tempSelectedModel, setTempSelectedModel] = useState(null);

    const { t } = useTranslation();

    const [itemsPerRequest, setItemsPerRequest] = useState(
        () => getObject("Select-Model-itemsPerPage", true) || 10
    );

    const modelVersionHeaders = [
        {
            label: t("active"),
            skipSort: true,
        },
        { label: t("version"), skipSort: true },
        { label: t("description"), skipSort: true },
        { label: t("publishedAt"), skipSort: true },
        { label: t("modelType"), skipSort: true },
        { label: t("range"), skipSort: true },
        {
            label: "R2 Test",
            element: (
                <span className="ml-2">
                    R<sup>2</sup> Test
                </span>
            ),
            skipSort: true,
        },
        { label: t("rmseTest"), skipSort: true },
        {
            label: t("r2Cv"),
            element: (
                <span className="ml-2">
                    R<sup>2</sup> CV
                </span>
            ),
            skipSort: true,
        },
        { label: t("rmseCv"), skipSort: true },
        { label: t("BiasTest"), skipSort: true },
        { label: t("RPDTest"), skipSort: true },
        { label: t("BiasCV"), skipSort: true },
        { label: t("RPDCV"), skipSort: true },
        { label: t("numberOfSamples"), skipSort: true },
        { label: t("supportedAccessories"), skipSort: true },
        { label: t("actions"), skipSort: true, classNames: "sticky-header" },
    ];

    useEffect(() => {
        if (selectedParameter?.value) {
            fetchModelParamterVersions(selectedParameter.value);
        }
    }, [selectedParameter]);

    useEffect(() => {
        if (selectedParameterVersion && product?.parameters) {
            const selectedParameterIds = selectedParameterVersion.map(
                (parameter) => parameter.parameterId
            );
            const fetchedParameterIds = product.parameters.map(
                (parameter) => parameter.parameterId
            );

            setIsForceUpgradeEnabled(
                fetchedParameterIds.every((paramId) =>
                    selectedParameterIds.includes(paramId)
                )
            );
        }
    }, [selectedParameterVersion, product]);

    const fetchModelParamterVersions = async (
        modelParameterId,
        offset = 0,
        itemsPerPage = itemsPerRequest
    ) => {
        setIsVersionTableLoading(true);
        const result = await ModelVersionsAPI.getModelVersions(
            modelParameterId,
            itemsPerPage,
            {},
            offset
        );
        setVersionsTotalCount(result?.totalCount);
        setVersionsTableData(result?.data);
        setIsVersionTableLoading(false);
    };

    const resetVersionsTable = () => {
        setSelectedVersion(null);
        setVersionsTableData([]);
        setVersionsCurrentPage(1);
        setVersionsTotalCount(0);
    };

    const handleModelSelection = async (model) => {
        if (selectedModel && !isEmpty(selectedParameterVersion)) {
            setParametersDictionary((prevState) => {
                const newParamList = prevState[selectedModel.value];
                return {
                    ...prevState,
                    [selectedModel.value]: [
                        ...newParamList,
                        ...removedParameters,
                    ],
                };
            });
        }
        setSelectedParameter({});
        resetVersionsTable();
        setSelectedParameterVersion([]);
        setRemovedParameters([]);
        setSelectedModel(model);
    };

    const handleParameterSelection = (parameter) => {
        setSelectedParameter(parameter);
        setSelectedVersion(null);
    };

    const handleAddParameter = () => {
        setRemovedParameters((prevState) => {
            return [...prevState, selectedParameter];
        });
        setParametersDictionary((prevState) => {
            const newParamList = prevState[selectedModel.value].filter(
                (params) => params.value !== selectedParameter.value
            );
            return { ...prevState, [selectedModel.value]: newParamList };
        });
        resetVersionsTable();
        setSelectedParameterVersion((prevState) => {
            return [
                ...prevState,
                {
                    label: `${selectedParameter.label}-${selectedVersion.version}`,
                    value: selectedVersion.id,
                    hidden: isSelectedParamHidden.value,
                    parameterId: selectedParameter.value,
                },
            ];
        });
        setSelectedParameter(null);
        setIsSelectedParamHidden(BOOLEAN_DROPDOWN.false);
    };

    const handleRemoveParameter = (tagName, index) => {
        setRemovedParameters((prevRemovedParams) => {
            const newRemovedParams = [...prevRemovedParams];
            const addedParameter = newRemovedParams.splice(index, 1);

            setParametersDictionary((prevDict) => {
                const selectedModelParams = prevDict[selectedModel.value];
                return {
                    ...prevDict,
                    [selectedModel.value]: [
                        ...selectedModelParams,
                        ...addedParameter,
                    ],
                };
            });

            return newRemovedParams;
        });

        setSelectedParameterVersion((prevState) => {
            let newAddedParameters = [...prevState];
            newAddedParameters.splice(index, 1);
            return newAddedParameters;
        });
    };

    const handleVersionsPageClick = (index) => {
        if (index !== setVersionsCurrentPage && selectedParameter?.value) {
            setVersionsCurrentPage(index);
            fetchModelParamterVersions(selectedParameter.value, index - 1);
        }
    };

    const handleItemsPerPage = (itemsPerPage) => {
        setItemsPerRequest(itemsPerPage);
        setVersionsCurrentPage(1);
        setObject("Select-Model-itemsPerPage", itemsPerPage, true);
        // call API to update the items per page
        fetchModelParamterVersions(selectedParameter.value, 0, itemsPerPage);
    };

    const renderParameterSelect = ({
        options,
        value,
        changeHandler,
        placeholder,
    }) => {
        return (
            <Select
                options={options}
                value={value}
                onChange={changeHandler}
                placeholder={placeholder}
                isDisabled={isEmpty(selectedModel)}
                theme={getReactSelectTheme}
            />
        );
    };

    const renderFields = () => {
        return (
            fields &&
            fields.map((field, index) => {
                if (!field.hide) {
                    const inputGroup = field.element();
                    if (inputGroup.rowFields) {
                        const fields = inputGroup.fields();
                        return (
                            <div className="flex gap-2" key={index}>
                                {fields.map((elem, index) => (
                                    <div
                                        className="tags input-group w-full"
                                        key={index}
                                    >
                                        <h4 className="uppercase text-xs required">
                                            {elem.label}
                                        </h4>

                                        {elem.input}
                                    </div>
                                ))}
                            </div>
                        );
                    }
                    return (
                        <div className="tags input-group" key={index}>
                            <h4 className="uppercase text-xs required">
                                {inputGroup.label}
                            </h4>

                            {inputGroup.input}
                        </div>
                    );
                }
            })
        );
    };
    const fields = [
        {
            element: () => {
                return {
                    label: (
                        <Trans i18nKey="publishModelStep1">Select Model</Trans>
                    ),
                    input: (
                        <Select
                            isDisabled={isNewVersion}
                            options={models}
                            value={isEmpty(selectedModel) ? "" : selectedModel}
                            onChange={(model) => {
                                if (
                                    selectedModel &&
                                    !isEmpty(selectedParameterVersion)
                                ) {
                                    setShowConfirmModal(true);
                                    setTempSelectedModel(model);
                                } else {
                                    handleModelSelection(model);
                                }
                            }}
                            placeholder={t("selectPlaceholder", {
                                entity: "Model",
                            })}
                            theme={getReactSelectTheme}
                        />
                    ),
                };
            },
        },
        {
            element: () => {
                return {
                    label: (
                        <Trans i18nKey="selectedParameters">
                            Selected Parameters
                        </Trans>
                    ),
                    input: (
                        <ProductTags
                            tags={selectedParameterVersion}
                            emptyElement={() => (
                                <div className="w-full">
                                    <SimpleNotification
                                        message={t(
                                            "selectedParametersEmptyMessage"
                                        )}
                                    />
                                </div>
                            )}
                            removeTagHandler={handleRemoveParameter}
                        />
                    ),
                };
            },
        },
        {
            element: () => {
                return {
                    rowFields: true,
                    fields: () => {
                        return [
                            {
                                label: (
                                    <Trans i18nKey="parameters">
                                        Parameters
                                    </Trans>
                                ),
                                input: (
                                    <>
                                        {renderParameterSelect({
                                            options:
                                                parametersDictionary[
                                                    selectedModel?.value
                                                ],
                                            value: isEmpty(selectedParameter)
                                                ? ""
                                                : selectedParameter,
                                            changeHandler:
                                                handleParameterSelection,
                                            placeholder: t(
                                                "selectPlaceholder",
                                                {
                                                    entity: "Parameters",
                                                }
                                            ),
                                        })}
                                    </>
                                ),
                            },
                            {
                                label: (
                                    <span>
                                        <Trans i18nKey="premium">Premium</Trans>
                                        <div className="text-xxs ml-0.5 inline">
                                            <InformationTooltip
                                                message={t(
                                                    "premiumParametersMessage"
                                                )}
                                            />
                                        </div>
                                    </span>
                                ),
                                input: (
                                    <>
                                        {renderParameterSelect({
                                            options:
                                                Object.values(BOOLEAN_DROPDOWN),
                                            value: isEmpty(
                                                isSelectedParamHidden
                                            )
                                                ? ""
                                                : isSelectedParamHidden,
                                            changeHandler:
                                                setIsSelectedParamHidden,
                                            placeholder: t(
                                                "selectPlaceholder",
                                                {
                                                    entity: "Hidden Parameters",
                                                }
                                            ),
                                        })}
                                    </>
                                ),
                            },
                        ];
                    },
                };
            },
        },
        {
            element: () => {
                return {
                    label: <Trans i18nKey="versions">Versions</Trans>,
                    input: (
                        <>
                            <LoadingOverlay
                                active={isVersionTableLoading}
                                text="Loading..."
                            >
                                <VersionTable
                                    headers={modelVersionHeaders}
                                    rows={versionsTableData}
                                    showSelected={true}
                                    selectedVersion={selectedVersion}
                                    setSelectedVersion={setSelectedVersion}
                                    emptyMessage={t("versionTableEmptyMessage")}
                                />
                                <Pagination
                                    currentPage={versionsCurrentPage}
                                    setCurrentPage={handleVersionsPageClick}
                                    totalCount={versionsTotalCount}
                                    sizePerPage={itemsPerRequest}
                                    handleItemsPerPage={handleItemsPerPage}
                                />
                            </LoadingOverlay>
                        </>
                    ),
                };
            },
        },
    ];

    return (
        <>
            <div className="steps step1 show">
                <TitleWithDescription
                    title={t("publishModelStep1")}
                    description={t("publishModelStep1Paragraph")}
                />
                <div className="card-table shadow flex flex-col">
                    {renderFields()}

                    {!isEmpty(selectedModel) && (
                        <button
                            className={`default-orange-outline-btn self-end ${
                                isEmpty(selectedModel) ||
                                isEmpty(selectedParameter) ||
                                isEmpty(selectedVersion)
                                    ? "disabled"
                                    : ""
                            }`}
                            onClick={handleAddParameter}
                            disabled={
                                isEmpty(selectedModel) ||
                                isEmpty(selectedParameter) ||
                                isEmpty(selectedVersion)
                            }
                        >
                            Add
                        </button>
                    )}
                </div>
            </div>
            <StepsNavButtons
                next={{
                    action: nextStep,
                    disabled:
                        !selectedParameterVersion.filter((elem) => !elem.hidden)
                            .length > 0,
                    label: nextStepButtonText,
                }}
            />

            <BaseAlertModal
                isVisible={showConfirmModal}
                setIsVisible={setShowConfirmModal}
                title={t("changeSelectedModelTitle")}
                message={t("changeSelectedModelMessage")}
                action={{
                    submitAction: () => handleModelSelection(tempSelectedModel),
                }}
                type="confirmation"
            />
        </>
    );
};

export default SelectModel;
